import React, { useRef, useState, useEffect, memo } from 'react';
import type { FieldRenderProps } from '../Input/Input';
import cn from 'classnames';
import noop from 'lodash/noop';
import styles from './checkbox.module.scss';

export interface ICheckboxProps extends Partial<FieldRenderProps<any, HTMLInputElement>> {
  input?: any,
  /**
     * Provide label text to be read by screen readers when interacting with the control
     */
  label?: string | React.ReactNode,
  /**
     * String passed as HTML class attribute
     */
  className?: string,
  /**
     * Provide an optional class name to be applied to the control node
     */
  controlClassName?: string,
  /**
     * Provide an optional class name to be applied to the label node
     */
  labelClassName?: string,
  /**
     * A name that identifies the element when submitting the form
     */
  name?: string,
  /**
     * HTML global `id` attribute, unique in the whole document
     */
  id?: string,
  type?: string,
  /**
     *
     * Sets the current state of the input
     */
  checked?: string | number | boolean,
  /**
   *
   * Sets the initial state of the input
   */
  defaultChecked?: string | number | boolean,
  /**
     * Enables indeterminate for the input. This is presentational only, the value of an indeterminate checkbox is not
     * included in form submissions.
     */
  supportIndeterminate?: boolean,
  /**
     * Marks the input as indeterminate. This is presentational only, the value of an indeterminate checkbox is not
     * included in form submissions.
     */
  indeterminate?: number | boolean,
  /**
     * Indicates that the control is not available for interaction
     */
  disabled?: boolean,
  onFocus?: (e: React.MouseEvent) => void,
  /**
     * A callback that is called when any of the inputs lose focus.
     */
  onBlur?: (e: React.MouseEvent) => void,
  /**
     * Defines a callback function which is called every time the checkbox state is changed
     */
  onChange?: (e: any) => void,
  /**
     * Defines a callback function which is called every time the checkbox state is clicked
     */
  onClick?: (e: React.MouseEvent) => void,
  /**
     * May be useful for elements that should not be navigated to directly using the Tab key
     */
  tabIndex?: string | number,
  /**
     * Indicates that the value cannot be changed
     */
  readOnly?: boolean,
  /**
     * Unique identifier for testing purposes
     */
  dataTest?: string,
  /**
     * Specifies whether a checkbox is checked
     */
  value?: string | number,

  /**
   *
   */
  nextValue?: (value: boolean, props: any) => boolean,
}

const Checkbox = (props: ICheckboxProps) => {
  const {
    label,
    className = '',
    controlClassName = '',
    supportIndeterminate = false,
    indeterminate = false,
    labelClassName = '',
    name,
    id,
    type = 'checkbox',
    disabled = false,
    readOnly,
    tabIndex = 0,
    onClick = noop,
    onFocus = noop,
    onChange = noop,
    onBlur = noop,
    dataTest,
    value,
    defaultChecked,
  } = props;

  const [checked, setChecked] = useState(!!(props.checked ?? props.input?.checked));
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = supportIndeterminate && indeterminate;
    }
  }, [supportIndeterminate, indeterminate]);

  useEffect(() => {
    setChecked(!!(props?.checked ?? props.input?.checked));
  }, [props.checked, props.input?.checked]);

  const nextValue = (oldValue: boolean | string | number) => !!oldValue === !!indeterminate
    || (!oldValue && !!indeterminate);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    let value = event.target.checked;
    if (supportIndeterminate) {
      if (typeof props.nextValue === 'function') {
        value = props.nextValue(!!checked, props);
      } else {
        value = nextValue(checked);
      }
    }
    setChecked(value);

    const newEvent = {
      target: {
        ...props,
        checked: value,
      },
      stopPropagation() {
        event.stopPropagation();
      },
      preventDefault() {
        event.preventDefault();
      },
    };

    onChange(newEvent);

    // Final-Form's onChange
    props.input?.onChange(newEvent);
  };

  const containerCls = cn(styles.checkbox, {
    [styles.isNoLabel]: !label,
  }, className);
  const controlCls = cn(styles.control, controlClassName);
  const labelCls = cn(styles.label, labelClassName);

  return (
    <label
      htmlFor={ id }
      className={ containerCls }
      data-test={ dataTest || name }
    >
      <input
        className={ styles.input }
        type={ type }
        id={ id }
        name={ name }
        readOnly={ readOnly }
        disabled={ disabled }
        // @ts-ignore
        tabIndex={ tabIndex }
        checked={ !!checked }
        defaultChecked={ !!defaultChecked }
        onClick={ onClick }
        onFocus={ onFocus }
        onBlur={ onBlur }
        onChange={ handleChange }
        ref={ checkboxRef }
        value={ value }
      />
      <div className={ controlCls } />
      { label && (
        <div className={ labelCls }>
          { label }
        </div>
      ) }
    </label>
  );
};

export default memo(Checkbox);
